import React from "react";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import Comments from "../PostComponents/Comments";
import ShareButton from "../PostComponents/ShareButton";
import PostDummy from "../Dummies/PostDummy";
import Ad from "../PostComponents/Ad";
import ShoppingCarousel from "../PostComponents/Carousel/ShoppingCarousel";
import Terms from "../PostComponents/Terms";
import Rating from "../Partials/Rating";
import Unmute from "../PostComponents/Unmute";
import ExitLink from "../PostComponents/ExitLink";
import RecommendedPosts from "../PostComponents/RecommendedPosts"
import ReelsPlayer from "../PostComponents/ReelsPlayer";

const waitFor = (predicate, { timeout = 2000, interval = 50 } = {}) =>
    new Promise((resolve, reject) => {
        const startTime = Date.now();
        const check = () => {
            if (predicate()) {
                resolve(true);
            } else if (Date.now() - startTime >= timeout) {
                reject(new Error("Timeout waiting for condition"));
            } else {
                setTimeout(check, interval);
            }
        };
        check();
    });

class Post extends React.Component {
    constructor(props) {
        super(props);

        this.post = this.props.data;

        this.playerHeading = this.post?.meta_data.jw_player_heading;
        this.playerID = this.post?.meta_data.jw_player_id ?? false;
        this.playlistID = this.post?.meta_data.jw_player_plalist_id ?? false;

        // Should we hide ads?
        let hideAdsEnabled = this.post?.meta_data._ana_hide_ads ?? false;
        let hideAds = false;
        let hideDuration =
            this.post?.meta_data._ana_hide_ads_duration_active ?? false;
        let hideUntil = this.post?.meta_data._ana_hide_ads_duration ?? false;
        if (hideAdsEnabled) {
            hideAds = true;

            if (hideDuration) {
                hideAds = new Date(hideUntil) > new Date();
            }
        }

        this.stopJWAutoPlay =
            this.post?.meta_data.jw_player_autoplay == "0" || false;
        /* Pass autostart as customProp because react-jw-player's isAutoPlay-prop only supports boolean value */
        this.jwPlayerCustomProps = {
            autostart: this.stopJWAutoPlay ? false : "viewable",
            setTimeEvents: true,
            floating: { dismissible: true, mode: "notVisible" }
        };
        this.activateUnmute = false;

        this.jwPlayerPosition = "top";

        this.state = {
            showComments: false,
            hideAds: hideAds,
            videoTitle: "",
            mute: false,
            showUnmute: false,
            hasSticky: true
        };

        this.showComments = this.showComments.bind(this);
        this.onReady = this.onReady.bind(this);
        this.onVideoLoad = this.onVideoLoad.bind(this);
        this.content = React.createRef();
        this.onUnmuteClick = this.onUnmuteClick.bind(this);
        this.onMuteToggle = this.onMuteToggle.bind(this);
        this.onDisplayClick = this.onDisplayClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.hasMuted = false;
        this.player = null;
        this.playerContainer = React.createRef();
        this.unmounted = false;
        this.play = true;
    }

    showComments(e) {
        e.preventDefault();

        this.setState({
            showComments: true
        });

        // Scroll to comments area (delay needed for element to be rendered first)
        setTimeout(() => {
            document
                .getElementById(`post-${this.post.id}-comments`)
                .scrollIntoView({
                    behavior: "smooth"
                });
        }, 50);
    }

    componentDidUpdate() {
        if (!this.content.current) {
            return;
        }
        const ratingContainerInContent = this.content.current.querySelector(
            ".outer_rating_container"
        );
        if (ratingContainerInContent) {
            ReactDOM.render(
                <Rating post={this.post} />,
                ratingContainerInContent
            );
        }
    }

    componentDidMount() {
        if (!this.post) {
            return;
        }
        // The global top_ad placement lives outside React, so hideAds must remove it explicitly
        if (this.state.hideAds && this.props.infinityIndex == 1) {
            document
                .querySelector('[data-aller-ad-placement="top_ad"]')
                ?.remove();
        }
        this.initJWPlayer();
        const polls = this.content.current.querySelectorAll(".poll");
        polls.forEach(p => {
            window.poll.loaded(p.dataset.questionId);
        });
        // rerender pinterest links
        window.PinUtils?.build?.(this.content.current) ?? false;
    }

    async initJWPlayer() {
        if (!this.playerID || !this.playlistID) {
            return;
        }
        try {
            await this.loadJWPlayerScript();
            await this.setupAdvertising();
        } catch (error) {
            console.warn(error);
            return;
        }
        const container = this.playerContainer.current;
        if (this.unmounted || !container || !window.jwplayer) {
            return;
        }

        this.player = window.jwplayer(container);
        this.player.setup({
            playlist: `https://cdn.jwplayer.com/v2/playlists/${this.playlistID}`,
            ...this.jwPlayerCustomProps
        });
        this.player.on("ready", this.onReady);
        this.player.on("playlistItem", this.onVideoLoad);
        this.player.on("mute", this.onMuteToggle);
        this.player.on("displayClick", this.onDisplayClick);
        this.player.on("play", this.onPlay);
    }

    loadJWPlayerScript() {
        return new Promise((resolve, reject) => {
            if (window.jwplayer) {
                resolve();
                return;
            }
            const scriptId = "jw-player-script";
            let script = document.getElementById(scriptId);
            if (!script) {
                script = document.createElement("script");
                script.id = scriptId;
                script.src = `https://cdn.jwplayer.com/libraries/${this.playerID}.js`;
                document.head.appendChild(script);
            }
            script.addEventListener("load", () => resolve());
            script.addEventListener("error", () =>
                reject(new Error("Failed to load JW Player library"))
            );
        });
    }

    // Add the advertising config to the JW Player custom props, which are merged into the
    // JW Player setup options. Requires the JW Player library to be loaded, as the
    // default schedule is read from it.
    async setupAdvertising() {
        const advertisingDefaults = window.jwplayer?.defaults?.advertising;
        if (!advertisingDefaults?.schedule) {
            return;
        }
        try {
            await waitFor(() => window.amav, { timeout: 5000 });
        } catch (error) {
            console.warn("window.amav not found after 5s.");
        }
        const amav = window.amav;
        let ppid = null;
        if (amav?.getVideoPpId) {
            try {
                ppid = amav.getVideoPpId();
            } catch (err) {
                console.log(err);
            }
        }
        let adTag = null;
        try {
            adTag = (await amav?.getAdTag({ startType: "autoPlay" })) ?? null;
        } catch (error) {
            console.warn("Failed to get ad tag from window.amav.", error);
        }

        try {
            const hostparts = window.location.host.split(".");
            const subdomain = hostparts[0];
            const domain = `${hostparts[1]}.${hostparts[2]}`;
            // Use the ad tag for all schedules, fall back to each schedule's own tag.
            this.jwPlayerCustomProps.advertising = {
                ...advertisingDefaults,
                schedule: advertisingDefaults.schedule.map(scheduled => ({
                    ...scheduled,
                    tag: this.addCustomTags(
                        adTag ??
                            scheduled.tag.replace(
                                `7783179/${domain}`,
                                `7783179/${domain}/${subdomain}`
                            ),
                        ppid
                    )
                }))
            };
        } catch (error) {
            console.error("Error while setting up advertising config:", error);
        }
    }

    addCustomTags(tag, ppid) {
        const url = new URL(tag);
        const search = new URLSearchParams(url.search);

        // Use getAll and join in case cust_params is defined multiple times.
        const customParamsString = search.getAll("cust_params").join("&");

        // Set out custom params.
        const customParams = new URLSearchParams(customParamsString);
        const verticals = window.aller_ga?.verticals || [];
        customParams.set("Vertikal", verticals.join(","));
        customParams.set("pos", "auto");
        customParams.set("demandmanager", "1");

        search.set("cust_params", customParams.toString());

        if (ppid) {
            search.set("ppid", ppid);
        }

        url.search = search.toString();
        return url.toString();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.player?.remove();
    }

    onReady(event) {
        this.setTrackingForJWPlayer();
        const brand = window.location.hostname.split(".").reverse()[1];
        this.setState({
            videoTitle:
                this.playerHeading ??
                `Utvalt från ${brand.charAt(0).toUpperCase() + brand.slice(1)}`
        });

        const whitelistedBlogsForAdsState = [
            "elsa.elle.se",
            "sandrabeijer.elle.se",
            "fredriksfika.allas.se"
        ];
        if (whitelistedBlogsForAdsState.includes(window.location.host)) {
            this.setAdsState(this.player);
        }
    }

    // set tracking for jwplayer
    setTrackingForJWPlayer() {
        window.cts.jwplayer = window.cts.jwplayer || [];
        window.cts.jwplayer.push({
            ...this.player,
            isTop: false
        });
    }
    // set Ads State
    setAdsState(player) {
        if (window.ama?.adStats) {
            player.on("adRequest", response => {
                try {
                    if (response) {
                        window.ama.adStats.add([
                            {
                                placement: "preroll",
                                gptEvent: "slotRequested"
                            }
                        ]);
                        window.ama.adStats.push();
                    }
                } catch (error) {
                    console.warn(`video adwatch error: `, error);
                }
            });
        }
    }

    onVideoLoad(event) {
        const {
            video_comm_link,
            video_comm_link_text
        } = this.player.getPlaylistItem();

        if (video_comm_link || video_comm_link_text) {
            this.setState({
                exitLink: {
                    url: video_comm_link,
                    text: video_comm_link_text
                }
            });
        }

        // this muting is needed as the package "react-jw-player" has issues
        // https://github.com/micnews/react-jw-player/issues/124
        if (this.hasMuted || this.stopJWAutoPlay) {
            return;
        }
        this.hasMuted = true;
        this.player.setMute(true);
    }

    onPlay() {
        this.activateUnmute = true;
        if (this.player.getMute()) {
            this.setState({ showUnmute: true });
        }
    }

    isRecipe() {
        return (
            (this.props.preview != 1 && this.post.meta_data.isRecipe) ||
            (this.props.preview == 1 && this.post.meta_data.isPreviewRecipe)
        );
    }

    addRecipeExtraDataIfRecipe(filtered) {
        const headingSearchSring = '<p class="headings-description">';
        const headingsIndex = filtered.indexOf(headingSearchSring);
        if (headingsIndex !== -1 && this.post.meta_data.recipeExtraHtml) {
            return filtered.replace(
                headingSearchSring,
                `${this.post.meta_data.recipeExtraHtml}${headingSearchSring}`
            );
        }
        const howtoSearchSring = '<div class="ingredients-list">';
        const howtoIndex = filtered.indexOf(headingSearchSring);
        if (howtoIndex !== -1 && this.post.meta_data.recipeExtraHtml) {
            return filtered.replace(
                howtoSearchSring,
                `${this.post.meta_data.recipeExtraHtml}${howtoSearchSring}`
            );
        }
        return filtered;
    }

    manipulateFilteredContent() {
        const sanitizeConfig = {
            ADD_TAGS: ["iframe"],
            ADD_ATTR: [
                "allow",
                "allowfullscreen",
                "frameborder",
                "onclick",
                "fluid",
                "target"
            ]
        };

        const ingress = this.post.meta_data["aller_post_introduction"]
            ? `<p><strong>${this.post.meta_data["aller_post_introduction"]}</strong></p>`
            : "";
        let filtered = this.post.filtered;
        filtered = this.addRecipeExtraDataIfRecipe(filtered);

        return DOMPurify.sanitize(ingress + filtered, sanitizeConfig);
    }

    onUnmuteClick() {
        this.player.setMute(false);
    }

    onMuteToggle({ mute }) {
        const showUnmute =
            this.activateUnmute && !this.state.showUnmute && mute
                ? { showUnmute: true }
                : {};
        this.setState({
            mute: mute,
            ...showUnmute
        });
    }

    onDisplayClick() {
        /* Remove small sticky on click */
        if (this.state.hasSticky && this.player.getFloating()) {
            this.setState({
                hasSticky: false
            });
        }
    }

    showVideoPlayer() {
        return (
            <div
                className={`js-jwplayer__container ${
                    this.state.hasSticky ? "jw-flag-mini-sticky" : ""
                }`}
            >
                <p className="js-jwplayer__title h2">{this.state.videoTitle}</p>
                <div
                    className="js-jwplayer__ui-container"
                    style={{
                        aspectRatio: '16/9',
                    }}
                    data-video-position={this.jwPlayerPosition}
                >
                    <div className="js-jwPlayer">
                        <div id={this.playerID} ref={this.playerContainer} />
                    </div>
                    {this.state.showUnmute && (
                        <Unmute
                            onClick={this.onUnmuteClick}
                            isMuted={this.state.mute}
                        ></Unmute>
                    )}

                    {this.state.exitLink && (
                        <ExitLink
                            url={this.state.exitLink.url}
                            text={this.state.exitLink.text}
                        />
                    )}
                </div>
            </div>
        );
    }

    hasReelsSettings() {
        return (
            typeof window.reels === "object" &&
            window.reels.playlistId &&
            window.reels.playlistId.length > 0 &&
            typeof window.reels.brand === "string" &&
            window.reels.brand.length > 0 &&
            typeof window.reels.site === "string" &&
            window.reels.site.length > 0
        );
    }

    render() {
        if (typeof this.post == "undefined") {
            return <PostDummy />;
        }

        return (
            <div
                className={
                    "col-12 mb-4" + (this.state.hideAds ? " hide-ads" : "")
                }
            >
                <div
                    className="post d-flex flex-column align-items-center"
                    data-post-id={this.post.id}
                >
                    <ShareButton
                        post={this.post}
                        showComments={this.showComments}
                    />
                    {(this.post.meta_data._ana_is_native == "1" ||
                        this.post.meta_data.is_affiliate == "1") && (
                        <div className="post_sticky">
                            {this.post.meta_data._ana_is_native == "1" &&
                                this.post.meta_data._ana_sponsor_brand_name && (
                                    <div className="banner banner--native">
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: this.post.meta_data
                                                    ._ana_sponsor_brand_name
                                            }}
                                        ></span>
                                        {this.post.meta_data
                                            ._ana_article_tracking_pixel && (
                                            <img
                                                src={
                                                    this.post.meta_data
                                                        ._ana_article_tracking_pixel
                                                }
                                                alt=""
                                            />
                                        )}
                                    </div>
                                )}
                            {this.post.meta_data.is_affiliate == "1" && (
                                <div className="banner banner--affiliate">
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: this.post.meta_data
                                                .affiliate_text
                                        }}
                                    ></span>
                                </div>
                            )}
                        </div>
                    )}
                    <span id={`post-${this.post.id}`} className="anchor" />
                    <a href={this.post.url} className="d-block mx-auto">
                        <h1>{this.post.title}</h1>
                    </a>
                    <div className="post_head">
                        {this.post.categories[0] && (
                            <div className="post_category">
                                <a href={`${this.post.categories[0].url}`}>
                                    {this.post.categories[0].name}
                                </a>
                            </div>
                        )}
                        <div className="post_date">
                            {this.post.date_time_text ? this.post.date_time_text : this.post.date_text}
                        </div>
                        {this.isRecipe() && (
                            // add rating component
                            <Rating post={this.post}></Rating>
                        )}
                    </div>

                    <Ad nbr="1" />

                    {this.props.infinityIndex == 1 &&
                        this.playerID &&
                        this.playlistID &&
                        this.jwPlayerPosition === "top" &&
                        this.showVideoPlayer()}

                    <div
                        className="post_content"
                        dangerouslySetInnerHTML={{
                            __html: this.manipulateFilteredContent()
                        }}
                        ref={this.content}
                    ></div>
                    <Terms
                        terms={[...this.post.categories, ...this.post.tags]}
                    />
                    {// only load on the first item in infinity scroll
                    this.props.infinityIndex == 1 &&
                        this.playerID &&
                        this.playlistID &&
                        this.jwPlayerPosition !== "top" &&
                        this.showVideoPlayer()}
                </div>
                <Comments
                    post={this.post}
                    showComments={this.state.showComments}
                    onShowComments={this.showComments}
                />

                {this.props.infinityIndex === 1 && this.hasReelsSettings() && (
                    <ReelsPlayer />
                )}

                {(this.props.infinityIndex != 1 || !this.hasReelsSettings()) && (
                    <RecommendedPosts
                        post={this.post}
                    />
                )}

                {// only load on the first item in infinity scroll
                this.props.infinityIndex == 1 && <ShoppingCarousel />}
            </div>
        );
    }
}

export default Post;
